import { requester } from "App";
import P from "components/P";
import CustomButton from "components/buttons/CustomButton";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { decode } from "shared/utils/Utils";
import VerticalFlex from "layouts/flex/VerticalFlex";
import FlexChild from "layouts/flex/FlexChild";
import Container from "layouts/container/Container";
import Dummy from "components/Dummy";

function PaymentComplete() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState();
    const [deviceCode, setDeviceCode] = useState();
    const [orderId, setOrderId] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const orderSent = useRef(false);
    const initialDataLoaded = useRef(false);

    // URL 파라미터 처리 및 초기 데이터 설정
    useEffect(() => {
        if (initialDataLoaded.current) return;

        try {
            const queryParams = new URLSearchParams(window.location.search);
            const dataParams = queryParams.get('data');
            const deviceCodeParams = queryParams.get('deviceCode');
            const orderIdParam = queryParams.get('orderId');

            // 필수 파라미터 검증
            if (!orderIdParam) {
                console.error("주문 ID가 없습니다");
                setError("주문 정보를 찾을 수 없습니다");
                navigate(`/ws/${id}/payment/fail`);
                return;
            }

            if (!dataParams || !deviceCodeParams) {
                console.error("필수 파라미터가 누락되었습니다: data 또는 deviceCode");
                setError("필요한 정보가 누락되었습니다");
                navigate(`/ws/${id}/payment/fail`);
                return;
            }

            // 데이터 설정
            const decodedString = decode(dataParams);
            setData(decodedString);
            setDeviceCode(deviceCodeParams);
            setOrderId(orderIdParam);
            initialDataLoaded.current = true;
        } catch (error) {
            console.error("초기 파라미터 처리 중 오류 발생:", error);
            setError("주문 정보를 처리하는 중 오류가 발생했습니다");
            navigate(`/ws/${id}/payment/fail`);
        }
    }, []); // 의존성 없음 - 컴포넌트 마운트 시 한 번만 실행

    // 주문 처리 함수 정의 - useCallback으로 메모이제이션
    const processOrder = useCallback(async () => {
        if (!data || !deviceCode || !orderId || orderSent.current) return;

        try {
            // 주문 정보 파싱
            const jsonArray = JSON.parse(data);

            // 변환된 결과를 담을 JSON 객체 생성
            const resultObject = {};

            // JSON 배열 순회
            for (let i = 0; i < jsonArray.length; i++) {
                const itemArray = jsonArray[i];
                const productCode = itemArray[0];
                const quantity = itemArray[1];
                const deviceId = itemArray[2];

                if (!resultObject[deviceId]) {
                    resultObject[deviceId] = [];
                }

                resultObject[deviceId].push({
                    productCode: productCode,
                    quantity: quantity
                });
            }

            // 객체를 배열로 변환
            const resultArray = Object.keys(resultObject).map(deviceId => ({
                deviceId: deviceId,
                items: resultObject[deviceId]
            }));

            // 주문 한 번만 전송하도록 플래그 설정
            orderSent.current = true;

            // 검증된 주문 API 호출
            const verifiedOrderData = {
                code: "order",
                orderId: orderId,
                roomId: id,
                content: resultArray,
                deviceCode: deviceCode
            };

            setIsLoading(true);

            return new Promise((resolve, reject) => {
                requester.orderMessage(verifiedOrderData,
                    (result) => {
                        setIsLoading(false);
                        if (result.code === 0) {
                            resolve(result);
                        } else {
                            reject(new Error("주문 처리 실패: " + (result.message || "알 수 없는 오류")));
                        }
                    },
                    (error) => {
                        setIsLoading(false);
                        reject(error);
                    }
                );
            });
        } catch (error) {
            console.error("주문 데이터 처리 중 오류 발생:", error);
            setIsLoading(false);
            throw error;
        }
    }, [data, deviceCode, orderId, id]);

    // 데이터가 준비되면 주문 처리 실행
    useEffect(() => {
        // 모든 필요 데이터가 있고 아직 처리되지 않았을 때만 실행
        if (data && deviceCode && orderId && !orderSent.current) {
            processOrder()
                .then(() => {
                    navigate(`/ws/${id}/payment/success`, { replace: true });
                })
                .catch((error) => {
                    console.error("주문 처리 실패:", error);
                    setError("주문 처리 중 오류가 발생했습니다");
                    navigate(`/ws/${id}/payment/fail`);
                });
        }
    }, [processOrder, navigate, id]);

    // 오류 화면 렌더링
    if (error) {
        return (
            <Container height={"85vh"}>
                <VerticalFlex>
                    <FlexChild justifyContent={"center"}>
                        <P size={18} weight={"bold"} color="red">오류가 발생했습니다</P>
                        <Dummy height={"15px"} />
                        <P size={16}>{error}</P>
                        <Dummy height={"30px"} />
                        <CustomButton
                            onClick={() => navigate(`/ws/${id}/main`)}
                            text="홈으로 이동"
                            style={{
                                width: "200px",
                                backgroundColor: "var(--main-color)",
                                color: "white",
                                border: "none",
                                borderRadius: "5px",
                                padding: "10px 20px"
                            }}
                        />
                    </FlexChild>
                </VerticalFlex>
            </Container>
        );
    }

    // 로딩 화면 렌더링
    return (
        <Container height={"85vh"}>
            <VerticalFlex>
                <FlexChild justifyContent={"center"}>
                    <P size={18} weight={"bold"}>결제 처리 중입니다. 잠시만 기다려주세요...</P>
                    {isLoading && (
                        <>
                            <Dummy height={"20px"} />
                            <div className="loading-spinner" style={{
                                width: "30px",
                                height: "30px",
                                border: "3px solid rgba(0, 0, 0, 0.1)",
                                borderRadius: "50%",
                                borderTop: "3px solid var(--main-color)",
                                animation: "spin 1s linear infinite"
                            }} />
                            <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
                        </>
                    )}
                </FlexChild>
            </VerticalFlex>
        </Container>
    );
}

export default PaymentComplete;